import { buttonVariants } from '@crowdex/ui/components/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@crowdex/ui/components/select';
import { Switch } from '@crowdex/ui/components/switch';
import { cn } from '@crowdex/ui/lib/utils';
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

/*
 * The rows of Settings, as the boards draw them (D16–D44, mobile 13–41): a small grey group
 * label above a card of rows, each row its title (and a line under it) on the left and its
 * control on the right. Everything applies at once: there is no Save button.
 */

/** The page's title and the line under it. */
export function SettingsPageHeader({
  title,
  description,
  aside,
}: {
  title: string;
  description?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <header className="mb-7 flex items-start gap-4 max-md:mb-5">
      <div className="min-w-0 flex-1">
        <h1 className="type-page-title text-foreground max-md:hidden">{title}</h1>
        {description && (
          <p className="type-body mt-2 text-muted-foreground max-md:mt-0">{description}</p>
        )}
      </div>
      {aside && <div className="shrink-0">{aside}</div>}
    </header>
  );
}

/** A group label and the card of rows under it. */
export function SettingsGroup({
  title,
  aside,
  children,
  className,
}: {
  title?: ReactNode;
  /** What sits at the end of the label row (a count, a lock). */
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('mb-7 max-md:mb-5', className)}>
      {title !== undefined && (
        <div className="mb-2 flex items-center gap-2 px-0.5 max-md:px-1">
          <h2 className="type-caption font-medium text-muted-foreground max-md:font-normal max-md:text-sm">
            {title}
          </h2>
          {aside && <span className="ml-auto flex items-center gap-2">{aside}</span>}
        </div>
      )}
      <div className="overflow-hidden rounded-2xl border bg-card">{children}</div>
    </section>
  );
}

/** One row of a card: what it is on the left, its control on the right. */
export function SettingRow({
  title,
  description,
  children,
  leading,
  className,
  descriptionClassName,
}: {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** An icon or logo before the title. */
  leading?: ReactNode;
  className?: string;
  descriptionClassName?: string;
}) {
  return (
    <div
      className={cn(
        // A narrow phone puts a field under its title rather than squeeze the title to a word per
        // line.
        'flex min-h-13 items-center gap-3 border-hairline border-b px-4 py-2.5 last:border-b-0 max-md:min-h-14 max-sm:has-[input,textarea]:flex-wrap',
        className,
      )}
      data-slot="setting-row"
    >
      {leading && <span className="flex shrink-0 items-center">{leading}</span>}
      <div className="min-w-0 flex-1 max-sm:basis-36">
        <div className="type-ui text-foreground max-md:text-base">{title}</div>
        {description && (
          <div
            className={cn(
              'type-caption mt-0.5 text-muted-foreground max-md:text-sm',
              descriptionClassName,
            )}
          >
            {description}
          </div>
        )}
      </div>
      {children !== undefined && (
        <div className="flex min-w-0 shrink-0 items-center gap-2 max-sm:ml-auto">{children}</div>
      )}
    </div>
  );
}

/** A row that opens something: the phone's sub-pages, a dialog. */
export function SettingLinkRow({
  title,
  description,
  value,
  leading,
  onSelect,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  value?: ReactNode;
  leading?: ReactNode;
  onSelect: () => void;
  children?: ReactNode;
}) {
  return (
    <button
      className="flex min-h-13 w-full items-center gap-3 border-hairline border-b px-4 py-2.5 text-left transition-colors last:border-b-0 hover:bg-foreground/4 focus-visible:bg-foreground/6 focus-visible:outline-none max-md:min-h-14"
      data-slot="setting-row"
      onClick={onSelect}
      type="button"
    >
      {leading && <span className="flex shrink-0 items-center">{leading}</span>}
      <span className="min-w-0 flex-1">
        <span className="type-ui block text-foreground max-md:text-base">{title}</span>
        {description && (
          <span className="type-caption mt-0.5 block text-muted-foreground max-md:text-sm">
            {description}
          </span>
        )}
      </span>
      {value !== undefined && (
        <span className="type-ui min-w-0 shrink truncate text-muted-foreground max-md:text-base">
          {value}
        </span>
      )}
      {children}
      <ChevronRight aria-hidden className="size-4 shrink-0 text-subtle-foreground" />
    </button>
  );
}

/** A select as a quiet pill (`Claude Code ⌄`), Radix underneath. */
export function SettingSelect({
  label,
  onValueChange,
  options,
  value,
  disabled,
  className,
}: {
  /** Its accessible name: the row's title. */
  label: string;
  onValueChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
  value: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <Select disabled={disabled} onValueChange={onValueChange} value={value}>
      <SelectTrigger
        aria-label={label}
        className={cn(
          'h-control-sm max-w-56 gap-1.5 rounded-control border-0 bg-control px-3 text-foreground type-ui shadow-none',
          className,
        )}
        size="sm"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function SettingToggle({
  checked,
  label,
  onChange,
  disabled,
}: {
  checked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <Switch aria-label={label} checked={checked} disabled={disabled} onCheckedChange={onChange} />
  );
}

/** A value shown at the end of a row; `tone` follows the charter's states. */
export function SettingValue({
  children,
  tone = 'muted',
}: {
  children: ReactNode;
  tone?: 'muted' | 'success' | 'attention' | 'running' | 'destructive' | 'foreground';
}) {
  return (
    <span
      className={cn(
        'type-ui truncate',
        tone === 'muted' && 'text-muted-foreground',
        tone === 'success' && 'text-success',
        tone === 'attention' && 'text-attention',
        tone === 'running' && 'text-running',
        tone === 'destructive' && 'text-destructive',
        tone === 'foreground' && 'text-foreground',
      )}
    >
      {children}
    </span>
  );
}

/** A small text below a card. */
export function SettingsNote({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn('type-caption -mt-4 mb-7 px-1 text-muted-foreground', className)}>
      {children}
    </p>
  );
}

/** A pill button with a thin border, the charter's secondary button. */
export function settingButtonClass(variant: 'outline' | 'primary' | 'control' = 'outline') {
  // The kit's buttons, for links and triggers that must stay their own element.
  return buttonVariants({
    size: 'sm',
    variant: variant === 'primary' ? 'default' : variant === 'control' ? 'secondary' : 'outline',
  });
}
